import { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import type { ThemeOptions, Components, Theme } from '@mui/material';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'theme-mode';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// ── Shared overrides (mode-independent) ──────────────────────────────
const sharedShape = { borderRadius: 12 };

const sharedTypography = {
  fontFamily: '"Inter", "SF Pro Display", "Roboto", "Helvetica", "Arial", sans-serif',
  h4: { fontWeight: 600 },
  h5: { fontWeight: 600 },
  h6: { fontWeight: 600 },
};

const sharedComponents: Components<Theme> = {
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        borderRadius: 8,
        fontWeight: 500,
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none' },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: { borderRadius: 12 },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: { borderRadius: 8, fontWeight: 500 },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: { textTransform: 'none' as const, fontWeight: 500 },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: { height: 3, borderRadius: 3 },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: 8 },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 12 },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: 16 },
    },
  },
};

// ── Light theme ──────────────────────────────────────────────────────
const lightTheme: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: { main: '#6366F1', light: '#818CF8', dark: '#4F46E5', contrastText: '#FFFFFF' },
    secondary: { main: '#8B5CF6', light: '#A78BFA', dark: '#7C3AED', contrastText: '#FFFFFF' },
    background: { default: '#F5F5F7', paper: '#FFFFFF' },
    text: { primary: '#1F2937', secondary: '#6B7280', disabled: '#9CA3AF' },
    divider: '#E5E7EB',
    error: { main: '#EF4444', light: '#FCA5A5', dark: '#DC2626' },
    warning: { main: '#F59E0B', light: '#FCD34D', dark: '#D97706' },
    success: { main: '#10B981', light: '#6EE7B7', dark: '#059669' },
    info: { main: '#3B82F6', light: '#93C5FD', dark: '#2563EB' },
    grey: {
      50: '#F9FAFB', 100: '#F3F4F6', 200: '#E5E7EB', 300: '#D1D5DB', 400: '#9CA3AF',
      500: '#6B7280', 600: '#4B5563', 700: '#374151', 800: '#1F2937', 900: '#111827',
    },
  },
  shape: sharedShape,
  typography: sharedTypography,
  components: {
    ...sharedComponents,
    MuiButton: {
      styleOverrides: {
        ...sharedComponents.MuiButton?.styleOverrides,
        contained: {
          '&:hover': { boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)' },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#1F2937',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#FFFFFF', borderRight: '1px solid #E5E7EB' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: '#EEF2FF',
            color: '#6366F1',
            '&:hover': { backgroundColor: '#E0E7FF' },
            '& .MuiListItemIcon-root': { color: '#6366F1' },
          },
          '&:hover': { backgroundColor: '#F3F4F6' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          border: '1px solid #E5E7EB',
          borderRadius: 16,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '16px !important',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          border: '1px solid #E5E7EB',
          '&:before': { display: 'none' },
          '&:first-of-type': { borderRadius: '16px !important' },
          '&:last-of-type': { borderRadius: '16px !important' },
          '& .MuiOutlinedInput-root': { backgroundColor: '#FFFFFF' },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 48,
          '&.Mui-expanded': { minHeight: 48 },
          '&:hover': { backgroundColor: '#EDEDEF' },
        },
        content: {
          margin: '8px 0',
          '&.Mui-expanded': { margin: '8px 0' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' },
        rounded: { borderRadius: 12 },
      },
    },
    MuiChip: {
      styleOverrides: {
        ...sharedComponents.MuiChip?.styleOverrides,
        outlined: { borderColor: '#E5E7EB' },
        colorSuccess: { backgroundColor: '#D1FAE5', color: '#059669' },
        colorWarning: { backgroundColor: '#FEF3C7', color: '#D97706' },
        colorError: { backgroundColor: '#FEE2E2', color: '#DC2626' },
        colorInfo: { backgroundColor: '#DBEAFE', color: '#2563EB' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#F9FAFB', fontWeight: 600, color: '#374151',
            borderBottom: '1px solid #E5E7EB',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: { root: { borderBottom: '1px solid #F3F4F6' } },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none' as const, fontWeight: 500, '&.Mui-selected': { color: '#6366F1' } },
      },
    },
    MuiTabs: {
      styleOverrides: { indicator: { backgroundColor: '#6366F1', height: 3, borderRadius: 3 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366F1' } },
        notchedOutline: { borderColor: '#E5E7EB' },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { '&.Mui-focused': { color: '#6366F1' } } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12 },
        standardSuccess: { backgroundColor: '#D1FAE5', color: '#065F46' },
        standardWarning: { backgroundColor: '#FEF3C7', color: '#92400E' },
        standardError: { backgroundColor: '#FEE2E2', color: '#991B1B' },
        standardInfo: { backgroundColor: '#DBEAFE', color: '#1E40AF' },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16 } },
    },
    MuiSkeleton: {
      styleOverrides: { root: { backgroundColor: '#F3F4F6' } },
    },
  },
};

// ── Dark theme ───────────────────────────────────────────────────────
const darkTheme: ThemeOptions = {
  palette: {
    mode: 'dark',
    primary: { main: '#818CF8', light: '#A5B4FC', dark: '#6366F1', contrastText: '#FFFFFF' },
    secondary: { main: '#A78BFA', light: '#C4B5FD', dark: '#8B5CF6', contrastText: '#FFFFFF' },
    background: { default: '#0F1117', paper: '#1A1D2E' },
    text: { primary: '#E5E7EB', secondary: '#9CA3AF', disabled: '#6B7280' },
    divider: 'rgba(255, 255, 255, 0.08)',
    error: { main: '#F87171', light: '#FCA5A5', dark: '#EF4444' },
    warning: { main: '#FBBF24', light: '#FCD34D', dark: '#F59E0B' },
    success: { main: '#34D399', light: '#6EE7B7', dark: '#10B981' },
    info: { main: '#60A5FA', light: '#93C5FD', dark: '#3B82F6' },
    grey: {
      50: '#1A1D2E', 100: '#1E2235', 200: '#262A3D', 300: '#353A50', 400: '#4B5168',
      500: '#6B7280', 600: '#9CA3AF', 700: '#D1D5DB', 800: '#E5E7EB', 900: '#F9FAFB',
    },
  },
  shape: sharedShape,
  typography: sharedTypography,
  components: {
    ...sharedComponents,
    MuiButton: {
      styleOverrides: {
        ...sharedComponents.MuiButton?.styleOverrides,
        contained: {
          '&:hover': { boxShadow: '0 4px 12px rgba(129, 140, 248, 0.3)' },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1D2E', color: '#E5E7EB',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#1A1D2E', borderRight: '1px solid rgba(255, 255, 255, 0.06)' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(129, 140, 248, 0.15)', color: '#A5B4FC',
            '&:hover': { backgroundColor: 'rgba(129, 140, 248, 0.2)' },
            '& .MuiListItemIcon-root': { color: '#A5B4FC' },
          },
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: 16, backgroundColor: '#1A1D2E',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '16px !important',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          backgroundColor: '#1A1D2E',
          '&:before': { display: 'none' },
          '&:first-of-type': { borderRadius: '16px !important' },
          '&:last-of-type': { borderRadius: '16px !important' },
          '& .MuiOutlinedInput-root': { backgroundColor: '#1A1D2E' },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 48,
          '&.Mui-expanded': { minHeight: 48 },
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
        },
        content: {
          margin: '8px 0',
          '&.Mui-expanded': { margin: '8px 0' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)', backgroundImage: 'none' },
        rounded: { borderRadius: 12 },
      },
    },
    MuiChip: {
      styleOverrides: {
        ...sharedComponents.MuiChip?.styleOverrides,
        outlined: { borderColor: 'rgba(255, 255, 255, 0.12)' },
        colorSuccess: { backgroundColor: 'rgba(52, 211, 153, 0.15)', color: '#34D399' },
        colorWarning: { backgroundColor: 'rgba(251, 191, 36, 0.15)', color: '#FBBF24' },
        colorError: { backgroundColor: 'rgba(248, 113, 113, 0.15)', color: '#F87171' },
        colorInfo: { backgroundColor: 'rgba(96, 165, 250, 0.15)', color: '#60A5FA' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#151825', fontWeight: 600, color: '#D1D5DB',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: { root: { borderBottom: '1px solid rgba(255, 255, 255, 0.06)' } },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none' as const, fontWeight: 500, '&.Mui-selected': { color: '#A5B4FC' } },
      },
    },
    MuiTabs: {
      styleOverrides: { indicator: { backgroundColor: '#818CF8', height: 3, borderRadius: 3 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#818CF8' } },
        notchedOutline: { borderColor: 'rgba(255, 255, 255, 0.12)' },
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { '&.Mui-focused': { color: '#818CF8' } } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12 },
        standardSuccess: { backgroundColor: 'rgba(52, 211, 153, 0.12)', color: '#6EE7B7' },
        standardWarning: { backgroundColor: 'rgba(251, 191, 36, 0.12)', color: '#FCD34D' },
        standardError: { backgroundColor: 'rgba(248, 113, 113, 0.12)', color: '#FCA5A5' },
        standardInfo: { backgroundColor: 'rgba(96, 165, 250, 0.12)', color: '#93C5FD' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, backgroundColor: '#1A1D2E', border: '1px solid rgba(255, 255, 255, 0.06)' },
      },
    },
    MuiSkeleton: {
      styleOverrides: { root: { backgroundColor: 'rgba(255, 255, 255, 0.06)' } },
    },
  },
};

// ── Provider ─────────────────────────────────────────────────────────
export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
    return 'light';
  });

  const toggleTheme = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  };

  const theme = useMemo(
    () => createTheme(mode === 'light' ? lightTheme : darkTheme),
    [mode],
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useThemeMode() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
}
